// external
import React, { useEffect, useState, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PropTypes } from "prop-types";
import { withTranslation } from "react-i18next";
import Button from "../button/Button";
import Select from "react-select";

import { useParams } from "react-router-dom";
import {
  InsertChart as InsertChartIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from "@material-ui/icons";
import classnames from "classnames";

import { useQuery } from "@tanstack/react-query";
import SwitchOnOff from "components/common/tools/SwitchOnOff";
import NavbarAvatarImage from "components/common/header/NavbarAvatarImage";

import InfoPanel from "../utils/InfoPanel";
import Spinner from "../utils/Spinner";
import SettingsAction from "components/utils/SettingsAction";
import VirtualizedSelectMenuList from "components/form/VirtualizedSelectMenuList";

import history from "components/common/navigation/AppHistory";

import { set_search_text } from "actions/PaginationActions";
import useAbility from "hooks/useAbility";
import { getTooltipMultiline } from "utils/data/Tooltip";
import ModalConfirm from "../common/tools/ModalConfirm";
import MediaAPI from "../../api/MediaAPI";
import ContentContext from "../../contexts/ContentContext";
import { CONTENT_TYPE, SMARTLIST_TYPE } from "../content/Type";
import { CONTENTS_ROUTE, PROJECTS_ROUTE } from "../routes/routes";
import MediaLink from "../contentTabs/media/MediaLink";
import Embedded from "../contentTabs/media/Embedded";
import { notifSuccess, notifError } from "../../actions/FlashMessageActions";
import SmartlistAPI from "../../api/SmartlistAPI";
import { getSelectClassNames } from "../form/Select";
import { APP_TOOLTIP_ID } from "../../constants/tooltip";

import "css/admin/utils/container-header.scss";
import "css/admin/utils/item-status.scss";
import Icon from "@mdi/react";
import { Loader2 } from "lucide-react";
import { mdiContentSave, mdiPlus } from "@mdi/js";

const { MASTER, LIVE, HOSTED, EXTERNAL } = CONTENT_TYPE;
const { STANDARD, AUTO } = SMARTLIST_TYPE;

function ContainerHeader({
  withBackBtn,
  canSave,
  hasComments,
  hasLikes,
  hasReports,
  isVerified,
  item,
  itemStatus,
  labelAttr,
  onChangeState,
  stateDisabledReason,
  onChangeLabel,
  customComponent,
  onSave,
  separator,
  t,
  loading,
  isSaving,
  onCreate,
  ownerRole,
  infoLinks,
  analyticsLink,
  showPage,
  previewLink,
  previewDisabled,
  onArchive,
  type,
  embedded,
  className,
}) {
  const dispatch = useDispatch();
  const { projectSlug } = useParams();
  const { onItemUpdate } = useContext(ContentContext);

  const { canWrite, canAccess } = useAbility();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState({});
  const [selectedProject, setSelectedProject] = useState({});
  const [modalContent, setModalContent] = useState({
    isOpen: false,
    body: "",
  });

  const orgaOptions = useSelector(
    (state) => state.fetchReducer.orga_config.data?.organization_options,
  );
  const projectList = useSelector((state) => state.fetchReducer.projects);

  const {
    project: canAccessProject,
    contentsAnalytics: canAccessContentsAnalytics,
  } = canAccess(orgaOptions);

  const getCopyMetadata = [LIVE, HOSTED, EXTERNAL].includes(item?.type);
  const getCopySmartlist =
    [STANDARD, AUTO].includes(item?.type) && type === "smartlist";

  const getIndexMetadatasMediaListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getIndexMetadatasMediaList,
    params: [],
  });

  const {
    isLoading: indexMetadatasMediaListLoading,
    data: indexMetadatasMediaList,
  } = useQuery({
    ...getIndexMetadatasMediaListQuery,
    enabled: getCopyMetadata,
  });

  const isMasterItem = item.type === MASTER;
  const showCopyMetadata = Boolean(getCopyMetadata && indexMetadatasMediaList);
  const showCopySmartlist = Boolean(getCopySmartlist && projectList);

  const titleInputRef = useRef();

  const mainClassName = classnames(
    separator
      ? "editor-container-header section-separator"
      : "editor-container-header",
    className,
  );

  const isLabelEditable = Boolean(onChangeLabel);

  useEffect(() => {
    if (isLabelEditable || item?.label) {
      headerTitleWidth(item[labelAttr]);
    }
  }, [item, isLabelEditable]);

  const handleConfirmModal = async () => {
    try {
      const metadataMedia = await MediaAPI.copyMetadataMedia({
        fromUUID: selectedMedia?.uuid,
        toUUID: item?.uuid,
      });
      onItemUpdate({
        ...item,
        ...metadataMedia,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopyMetadata = (media) => {
    setSelectedMedia(media);
    if (media) {
      const label = media?.label;
      setModalContent({
        isOpen: true,
        body: t("popup:bodyCopyMetadata", { label }),
      });
    }
  };
  const handleCopySmartlist = (projectSelected) => {
    setSelectedProject(projectSelected);
    if (projectSelected) {
      const name = projectSelected?.name;
      setModalContent({
        isOpen: true,
        body: t("popup:bodyCopySmartlist", { name }),
      });
    }
  };
  const handleConfirmModalCopySmartlist = async () => {
    try {
      await SmartlistAPI.copySmartlistToProject({
        projectUUID: selectedProject?.uuid,
        smartlistUUID: item?.uuid,
        projectSlug,
      });
      dispatch(notifSuccess(t("copySmartlistSuccess")));
    } catch (error) {
      console.error(error);
      dispatch(notifError(t("copySmartlistError")));
    }
  };
  const handleCloseModal = () => {
    setModalContent({
      isOpen: false,
      body: "",
    });
  };

  const goToComments = () => {
    dispatch(set_search_text(item.uuid, "comments"));
    history.push(`${PROJECTS_ROUTE}/${projectSlug}/comments`);
  };

  const changeLabel = (target) => {
    headerTitleWidth(target.value);
    onChangeLabel(target);
  };

  const headerTitleWidth = (text) => {
    const eltTitle = document.getElementById("header-title");
    const eltHidden = document.getElementById("header-title-hidden");

    if (eltHidden) eltHidden.textContent = `${text}.`;
    if (eltTitle?.style && eltHidden)
      eltTitle.style.width = `${eltHidden.offsetWidth}px`;
  };

  const showSettingsAction = () => {
    let settings = null;

    switch (type) {
      case "edu":
        settings = (
          <SettingsAction
            delete_body={t("editor-users:deleteBody", { email: item.edu_name })}
            edit_endpoint={`/users/edit/${item.uuid}`}
            endpoint="/api/user/v3/admin/users/"
            className="container-actions"
            get_status={false}
            hide_edit
            item={item}
            type="EDU"
            url_history={`${PROJECTS_ROUTE}/${projectSlug}/institutions`}
          />
        );
        break;
      case "user":
        if (!canAccessProject.usersAnalytics) break;

        settings = (
          <SettingsAction
            delete_body={t("editor-users:deleteBody", { email: item.email })}
            edit_endpoint={`/users/edit/${item.uuid}`}
            endpoint="/api/user/v3/admin/users/"
            className="container-actions"
            get_status={false}
            hide_edit
            item={item}
            type="USER"
            url_history={`${PROJECTS_ROUTE}/${projectSlug}/users`}
          />
        );
        break;
      case "content":
        if (!canWrite("contents", item.type)) break;

        settings = (
          <SettingsAction
            archive_body={t("editor-content:archiveBody", {
              content: item.label,
            })}
            delete_body={t("editor-content:deleteBody", {
              content: item.label,
            })}
            warning={
              <p>
                {" "}
                {t("editor-content:warning_1")}{" "}
                <strong>{t("editor-content:warning_2_bold")}</strong>.{" "}
                {t("editor-content:warning_3")}{" "}
              </p>
            }
            edit_endpoint={`${CONTENTS_ROUTE}/${item.type}/${item.uuid}`}
            endpoint="/api/media/v6/admin/medias/"
            className="container-actions"
            get_status={false}
            hide_edit
            item={item}
            onArchive={onArchive}
            type="INDEX_MEDIA"
            // because we have master media in /contents/hosted
            url_history={`${CONTENTS_ROUTE}/${
              isMasterItem ? "hosted" : item.type
            }`}
          />
        );
        break;
    }

    return settings;
  };

  const ownerTooltip = [];
  if (item.owner?.name) {
    ownerTooltip.push(item.owner?.name);
  }
  if (item.owner?.company) {
    ownerTooltip.push(item.owner?.company);
  }
  if (item.owner?.email) {
    ownerTooltip.push(item.owner?.email);
  }

  const displaySwitch = onChangeState && canSave;
  const displayAnalytics =
    !isMasterItem && analyticsLink && canAccessContentsAnalytics;

  const selectClassNames = getSelectClassNames();

  return (
    <div className={mainClassName} data-testid="ContainerHeader">
      <div className="flex items-center">
        <div className="editor-header-body">
          {withBackBtn && (
            <span
              className="editor-header-back-btn"
              onClick={() => history.goBack()}
            >
              <i className="material-icons editor-multi-icons">arrow_back</i>
            </span>
          )}
          {onChangeLabel && (
            <div className="editor-header-label-edit">
              <input
                disabled={!canSave}
                type="text"
                className="editor-header-input-title"
                id="header-title"
                value={item[labelAttr]}
                placeholder=""
                aria-label=""
                maxLength="128"
                onChange={(e) => {
                  changeLabel(e.target);
                }}
                ref={titleInputRef}
              />
              <span
                id="header-title-hidden"
                className="editor-header-input-title editor-header-title-hidden"
              >
                {item[labelAttr]}
              </span>
              {canSave && (
                <span
                  className="editor-header-icon-edit"
                  onClick={() => {
                    titleInputRef.current.focus();
                  }}
                >
                  <i className="material-icons editor-multi-icons">edit</i>
                </span>
              )}
            </div>
          )}

          {!onChangeLabel && (
            <div className="editor-header-label-edit editor-header-label-disabled">
              <span className="editor-header-input-title" id="header-title">
                {item[labelAttr]}
              </span>
            </div>
          )}

          {customComponent}

          {ownerRole && (
            <div className="editor-header-indicator editor-tooltip editor-tooltip-center editor-tooltip-multiline">
              <NavbarAvatarImage avatar_url={item.owner?.avatar_url} />
              {ownerTooltip.length > 0 && getTooltipMultiline(ownerTooltip)}
            </div>
          )}

          {displaySwitch && (
            <>
              {itemStatus && (
                <div className="editor-header-indicator">
                  <span
                    className={`editor-item-status item-status-${itemStatus}`}
                  >
                    {" "}
                  </span>
                </div>
              )}
              <div
                data-tooltip-id={APP_TOOLTIP_ID}
                data-tooltip-content={stateDisabledReason}
              >
                <SwitchOnOff
                  name="enable-switch"
                  iconName="power_settings_new"
                  checked={itemStatus !== "stop" && itemStatus !== "disabled"}
                  disabled={Boolean(stateDisabledReason)}
                  size="sm"
                  onChange={(enabled) => {
                    onChangeState(enabled);
                  }}
                />
              </div>
            </>
          )}

          {(item.is_archivable || item.is_deletable || type === "user") && (
            <div className="icon-button-actions">{showSettingsAction()}</div>
          )}

          {showPage && (
            <div className="editor-show-page">
              <a
                className="icon-show-page"
                rel="noopener noreferrer"
                target="_blank"
                href={showPage}
              >
                <i className="material-icons">remove_red_eye</i>
              </a>
            </div>
          )}

          {!!hasComments && canAccessProject.community && (
            <div
              className="icon-button icon-button-actions"
              onClick={goToComments}
            >
              <i className="material-icons">forum</i>
              <span>{hasComments}</span>
            </div>
          )}

          {!!isVerified && (
            <div className="icon-button-actions">
              <i className="material-icons">verified</i>
            </div>
          )}

          {!!hasReports && (
            <div className="icon-button-actions">
              <i className="material-icons">flag</i>
              <span>{hasReports}</span>
            </div>
          )}

          {!!hasLikes && (
            <div className="icon-button-actions">
              <i className="material-icons">favorite</i>
              <span>{hasLikes}</span>
            </div>
          )}

          {infoLinks && <InfoPanel links={infoLinks} />}
        </div>
        {showCopyMetadata && (
          <>
            <div className="editor-select-copy">
              <Select
                classNamePrefix="slct"
                classNames={selectClassNames}
                components={{ MenuList: VirtualizedSelectMenuList }}
                placeholder={t("copy_metadata")}
                onChange={handleCopyMetadata}
                options={indexMetadatasMediaList?.map((media) => ({
                  ...media,
                  value: media.uuid,
                }))}
                noOptionsMessage={t("noResult")}
              />
            </div>
            <ModalConfirm
              open={modalContent.isOpen}
              onConfirm={handleConfirmModal}
              onClose={handleCloseModal}
              body={modalContent.body}
              confirmText={t("modal:confirm")}
              cancelText={t("modal:cancel")}
              title={t("copy_metadata")}
            />
          </>
        )}
        {showCopySmartlist && (
          <>
            <div className="editor-select-copy">
              <Select
                classNamePrefix="slct"
                classNames={selectClassNames}
                components={{ MenuList: VirtualizedSelectMenuList }}
                placeholder={t("copy_smartlist")}
                onChange={handleCopySmartlist}
                options={projectList.data.map((project) => ({
                  ...project,
                  value: project.uuid,
                  label: project.name,
                }))}
                noOptionsMessage={t("noResult")}
              />
            </div>
            <ModalConfirm
              open={modalContent.isOpen}
              onConfirm={handleConfirmModalCopySmartlist}
              onClose={handleCloseModal}
              body={modalContent.body}
              confirmText={t("modal:confirm")}
              cancelText={t("modal:cancel")}
              title={t("copy_smartlist")}
            />
          </>
        )}
        {canSave &&
          onSave &&
          (loading ? (
            <Spinner />
          ) : (
            <Button
              isDisabled={isSaving}
              size="md"
              onClick={onSave}
              type="button"
            >
              {isSaving ? (
                <Loader2 className="size-6 animate-spin" />
              ) : (
                <Icon path={mdiContentSave} size={1} />
              )}
              {t("save")}
            </Button>
          ))}

        {onCreate && (
          <>
            {onCreate.options ? (
              <div className="ml-4">
                <Button
                  size="md"
                  type="button"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  dropdownItemList={onCreate.options}
                >
                  <Icon path={mdiPlus} size={1} />
                  {onCreate.label}
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                size="md"
                className="ml-4"
                onClick={onCreate.onClick}
              >
                <Icon path={mdiPlus} size={1} />
                {onCreate.label}
              </Button>
            )}
          </>
        )}
      </div>
      {(previewLink || displayAnalytics || embedded) && (
        <div className="flex gap-[0.78125rem]">
          {previewLink && (
            <MediaLink
              link={previewLink}
              disabled={previewDisabled}
              label={t("viewLink")}
              icon={<RemoveRedEyeIcon />}
              isExternal
            />
          )}
          {displayAnalytics && (
            <MediaLink
              link={analyticsLink}
              label={t("analytics")}
              icon={<InsertChartIcon />}
            />
          )}
          {embedded?.length > 0 && <Embedded items={embedded} />}
        </div>
      )}
    </div>
  );
}

ContainerHeader.propTypes = {
  previewLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }),
    ),
  ]),
  previewDisabled: PropTypes.bool,
  analyticsLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }),
    ),
  ]),
  canSave: PropTypes.bool,
  hasComments: PropTypes.number,
  hasLikes: PropTypes.number,
  hasReports: PropTypes.number,
  isVerified: PropTypes.bool,
  item: PropTypes.object,
  itemStatus: PropTypes.string,
  stateDisabledReason: PropTypes.string,
  labelAttr: PropTypes.string,
  onArchive: PropTypes.func,
  withBackBtn: PropTypes.bool,
  onChangeLabel: PropTypes.func,
  onChangeState: PropTypes.func,
  onSave: PropTypes.func,
  onCreate: PropTypes.object,
  ownerRole: PropTypes.bool,
  showPage: PropTypes.string,
  loading: PropTypes.bool,
  isSaving: PropTypes.bool,
  separator: PropTypes.bool,
  t: PropTypes.func.isRequired,
  customComponent: PropTypes.element,
  infoLinks: PropTypes.shape({
    en: PropTypes.string.isRequired,
    fr: PropTypes.string.isRequired,
  }),
  type: PropTypes.string,
  embedded: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        iframeCode: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }),
    ),
  ]),
  className: PropTypes.string,
};

ContainerHeader.defaultProps = {
  canSave: true,
  labelAttr: "label",
};

export default withTranslation("editor-utils")(ContainerHeader);
